import React, { FC, ReactElement, useState } from "react";
import { useDebounce } from "use-debounce";

import {
  Autocomplete,
  ListItem,
  ListItemText,
  Stack,
  TextFieldProps,
  Tooltip,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";

import { useDispatch, useSelector } from "../../store";
import { useListKeyPairsQuery } from "../../store/enhancedApi";
import { EntityInfo } from "../../store/pomeriumApi";
import { PickerType } from "../../types/KeyPairPicker";
import { ReduxFormProps } from "../../types/ReduxForm";
import DetailViewDialog from "../../views/certificates/components/DetailViewDialog";
import { LaunchIcon } from "../icons";
import TextField from "../styled/TextField";

type FilterItem = {
  field: string;
  operator: string;
  value: any;
};

const clientCertificateFilterItem: FilterItem = {
  field: "isClientCertificate",
  operator: "is",
  value: true,
};
const certificateAuthorityFilterItem: FilterItem = {
  field: "isCertificateAuthority",
  operator: "is",
  value: true,
};

export type KeyPairPickerProps = TextFieldProps &
  ReduxFormProps & {
    pickerType: PickerType;
  };

export const KeyPairPicker: FC<React.PropsWithChildren<KeyPairPickerProps>> = ({
  pickerType,
  getter,
  setter,
  path,
  disabled,
}): ReactElement => {
  const dispatch = useDispatch();
  const keyPairId = useSelector(getter(path, "")) as string;
  const [showDetail, setShowDetail] = useState(false);
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [inputValue, setInputValue] = useState("");
  const [delayedInputValue, { isPending }] = useDebounce(inputValue, 250);
  const [open, setOpen] = useState(false);

  const { data: result, isFetching } = useListKeyPairsQuery(
    {
      organizationId,
      namespaceId: clusterId,
      includeDescendants: true,
      filter: JSON.stringify({
        items: [
          pickerType === PickerType.ClientCertificate
            ? clientCertificateFilterItem
            : certificateAuthorityFilterItem,
        ].concat([
          { field: "name", operator: "contains", value: delayedInputValue },
        ]),
      }),
      limit: 10,
    },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const isLoading = isFetching || isPending();
  let certificates: EntityInfo[] = [];
  if (!open || !isLoading) {
    certificates =
      result?.rows.map((c) => ({
        id: c.id,
        name: c.name || "",
        updatedAt: c.updatedAt,
      })) || [];
    certificates.push({ id: "", name: "None", updatedAt: "" });
  }
  const selectedCertificate = open
    ? null
    : certificates.find((c) => c.id === keyPairId) || null;

  const onOpenDetail = (): void => {
    setShowDetail(true);
  };
  const onCloseDetail = (): void => {
    setShowDetail(false);
  };
  const onChange = (
    _evt: React.SyntheticEvent,
    certificate: EntityInfo | null,
  ): void => {
    dispatch(setter({ path, value: certificate?.id || undefined }));
  };
  const onInputChange = (
    _evt: React.SyntheticEvent,
    newInputValue: string,
  ): void => {
    setInputValue(newInputValue);
  };
  const onOpen = (): void => {
    setOpen(true);
  };
  const onClose = (): void => {
    setOpen(false);
  };

  return (
    <>
      <DetailViewDialog
        open={showDetail}
        keyPairId={keyPairId}
        onClose={onCloseDetail}
      />
      <Stack direction="row" spacing={1}>
        <Autocomplete<EntityInfo>
          fullWidth
          size="small"
          disabled={disabled}
          loading={isLoading}
          value={selectedCertificate}
          options={certificates}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(x, y) => x?.id === y?.id}
          renderInput={(params) => <TextField {...params} />}
          renderOption={(props, certificate) => {
            const { key, ...optionProps } = props;
            return (
              <ListItem key={key} {...optionProps}>
                <ListItemText
                  primary={certificate.name}
                  slotProps={{
                    primary: {
                      sx: certificate.id ? {} : { fontStyle: "italic" },
                    },
                  }}
                />
              </ListItem>
            );
          }}
          open={open}
          onChange={onChange}
          onInputChange={onInputChange}
          onOpen={onOpen}
          onClose={onClose}
          blurOnSelect
        />
        {keyPairId && (
          <Tooltip title="Certificate Details" aria-label="Certificate Details">
            <IconButton size="small" onClick={onOpenDetail}>
              <LaunchIcon color={keyPairId ? "primary" : "disabled"} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </>
  );
};
export default KeyPairPicker;
